import React, { useEffect, useRef } from "react";
import FormattedMessage from "components/FormattedMessage";
import { Field } from "redux-form";
import { Box, Label as RebassLabel, Checkbox } from "ui-components";
import Label from "./Label";
import FieldError from "./FieldError";
import { iconThemes } from "ui-components/theme";
import isFunction from "lodash/isFunction";
import get from "lodash/get";
import uniqueId from "lodash/uniqueId";
import { handleRelatedTargetAfterBlur } from "utils/helpers";

const RenderField = ({
  input,
  label,
  type,
  placeholder,
  meta: { touched, error },
  render,
  data: { checked_message },
  checkBoxOnly,
  onBlur,
  sx,
  iconColor,
  disabled = false,
  defaultChecked,
  onClick,
  required,
  optional
}) => {
  const checkboxId = useRef(uniqueId("checkbox_"));

  useEffect(() => {
    // this is used instead of input.checked because it will
    // trigger textfield to be disabled after this field is prefilled.
    // for example middle name field.
    defaultChecked && input.onChange(defaultChecked);
    // eslint-disable-next-line
  }, [defaultChecked]);
  const styleOverride = sx || {};
  const handleOnBlur = (event, newValue, previousValue, name) => {
    if (isFunction(onBlur)) {
      onBlur(event, newValue, previousValue, name);
    } else if (isFunction(input.onBlur)) {
      input.onBlur(event, newValue, previousValue, name);
    }
    handleRelatedTargetAfterBlur(event);
  };

  return (
    <>
      {label && (
        <Box>
          <RebassLabel alignItems="center" htmlFor={checkboxId.current}>
            {/* 
              force checkbox containers to be at 32px width, without
              initial flex-grow or flex-shrink settings intervening 
              for cases where labels are wordy
            */}
            <Box sx={{ flexBasis: "32px", ...styleOverride }}>
              <Checkbox
                id={checkboxId.current}
                value={input.value || defaultChecked}
                onClick={onClick}
                {...input}
                onBlur={handleOnBlur}
                placeholder={placeholder}
                type={type}
                sx={{ color: iconColor }}
                disabled={disabled}
              />
            </Box>
            {/*
              wider gutter <400px viewport width, better spacing 
              (margin of error) between label and checkbox
            */}
            <Box
              sx={{
                flexBasis: "calc(100% - 32px)",
                "@media all and (max-width: 399px)": {
                  paddingLeft: "1.125rem"
                },
                paddingLeft: "1rem",
                ...styleOverride
              }}
            >
              {!required ? (
                <FormattedMessage html id={label} />
              ) : (
                <Label
                  label={<FormattedMessage html id={label} />}
                  sx={{ marginBottom: "0 !important" }}
                  required={required}
                  optional={optional}
                  htmlFor={checkboxId.current}
                />
              )}
            </Box>
          </RebassLabel>
        </Box>
      )}

      {checkBoxOnly && (
        <Box>
          <RebassLabel alignItems="center">
            <Checkbox
              {...input}
              onBlur={handleOnBlur}
              placeholder={placeholder}
              type={type}
              onClick={onClick}
              sx={{ color: iconColor }}
              disabled={disabled}
            />
          </RebassLabel>
        </Box>
      )}

      {input.checked && checked_message && (
        <RebassLabel>
          <FormattedMessage html id={checked_message} />
        </RebassLabel>
      )}

      <FieldError touched={touched} error={error} />

      {render && render({ isChecked: input.checked, message: checked_message })}
    </>
  );
};

const CheckboxField = props => {
  const {
    field,
    pattern,
    label,
    placeholder,
    onChange,
    normalize,
    render,
    data,
    sx,
    checkBoxOnly = false,
    themeColors,
    disabled = false,
    defaultChecked,
    onClick = () => {},
    required,
    optional
  } = props;
  const name = field || "unnamedcheckbox";
  const iconColor = get(themeColors, "icon", iconThemes.blue);
  return (
    <Box mb={8} data-testid={name}>
      <Field
        defaultChecked={defaultChecked}
        name={name}
        component={RenderField}
        type="checkbox"
        pattern={pattern}
        label={label}
        placeholder={placeholder}
        onChange={onChange}
        normalize={normalize}
        render={render}
        data={data}
        sx={sx}
        onClick={onClick}
        disabled={disabled}
        checkBoxOnly={checkBoxOnly}
        iconColor={iconColor}
        required={required}
        optional={optional}
      />
    </Box>
  );
};

CheckboxField.defaultProps = {
  data: {}
};

export default CheckboxField;
